import type { AuditLog, EventType, TargetType } from '#dn-types/audit-log.ts';

const FIELD_LABELS: Partial<Record<TargetType, Record<string, string>>> = {
  host: {
    name: 'name',
    roleID: 'role',
    ipAddress: 'IP',
    ipAddresses: 'IP',
    networkID: 'network',
    listenPort: 'listen port',
    isLighthouse: 'lighthouse',
    isRelay: 'relay',
    isBlocked: 'blocked',
    staticAddresses: 'static addresses',
    tags: 'tags',
    configOverrides: 'config overrides',
  },
  role: {
    name: 'name',
    description: 'description',
    firewallRules: 'firewall rules',
  },
  tag: {
    name: 'name',
    description: 'description',
    priority: 'priority',
    configOverrides: 'config overrides',
    firewallRules: 'firewall rules',
    routeSubscriptions: 'route subscriptions',
  },
  network: {
    name: 'name',
    cidr: 'CIDR',
    cidrV4: 'IPv4 CIDR',
    cidrV6: 'IPv6 CIDR',
    lighthousesAsRelays: 'lighthouses as relays',
    disableManagedLighthouses: 'managed lighthouses',
  },
  oidcProvider: {
    enabled: 'enabled',
    clientID: 'client ID',
    issuerURL: 'issuer URL',
  },
  endpointOIDC: {
    name: 'name',
    roleID: 'role',
    enabled: 'enabled',
    clientID: 'client ID',
    issuerURL: 'issuer URL',
    domainID: 'domain',
    networkID: 'network',
    authValidityHours: 'auth validity',
  },
  organization: {
    name: 'name',
  },
  domain: {
    domainName: 'domain',
    verified: 'verified',
  },
  googleIdP: {
    adminEmail: 'connected by',
    domain: 'Workspace domain',
    googleCustomerID: 'Google customer ID',
    grantedScopes: 'granted scopes',
  },
  apiKey: {
    name: 'name',
    permissions: 'permissions',
    expiresAt: 'expiration',
  },
  joinKey: {
    name: 'name',
    networkID: 'network',
    roleID: 'role',
    tags: 'tags',
    autoAssignCIDRs: 'address pools',
    expiresAt: 'expiration',
  },
  user: {
    email: 'email',
  },
};

const IDENTITY_FIELDS: Partial<Record<TargetType, string[]>> = {
  host: ['name', 'ipAddress', 'ipAddresses'],
  role: ['name'],
  tag: ['name'],
  network: ['name', 'cidr', 'cidrV4', 'cidrV6'],
  apiKey: ['name'],
  joinKey: ['name'],
  user: ['email'],
  oidcProvider: ['issuerURL'],
  endpointOIDC: ['name'],
  organization: ['name'],
  domain: ['domainName'],
  googleIdP: ['domain', 'adminEmail'],
};

type SummaryBehavior = 'create' | 'delete' | 'update' | 'host-state' | 'skip';

const EVENT_BEHAVIOR: Record<EventType, SummaryBehavior> = {
  CREATED: 'create',
  UPDATED: 'update',
  DELETED: 'delete',
  DELETED_TOTP: 'skip',
  UPDATED_TOTP: 'skip',
  CREATED_TOTP: 'skip',
  SUCCEEDED_AUTH: 'skip',
  FAILED_AUTH: 'skip',
  ENROLLED: 'skip',
  RENEWED: 'skip',
  RENEWED_NETWORK_ACCESS: 'skip',
  CREATED_ENROLL_CODE: 'create',
  SET_NETWORK_CA: 'skip',
  BLOCKED_HOST: 'host-state',
  UNBLOCKED_HOST: 'host-state',
  SET_OVERRIDES: 'update',
  UPDATED_SUBSCRIPTION: 'update',
  CHANGED_DOMAIN_VERIFICATION: 'update',
  CREATED_WEBAUTHN: 'skip',
  DELETED_WEBAUTHN: 'skip',
  UPDATED_WEBAUTHN: 'skip',
  ENABLED_MANAGED_LIGHTHOUSE: 'skip',
  SIGNED_MANAGED_LIGHTHOUSE: 'skip',
};

function camelToWords(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase();
}

function getFieldLabel(targetType: TargetType, fieldKey: string): string {
  return FIELD_LABELS[targetType]?.[fieldKey] ?? camelToWords(fieldKey);
}

function isSimpleValue(val: unknown): val is string | number | boolean {
  return typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean';
}

function formatSimpleValue(val: string | number | boolean): string {
  if (typeof val === 'string') return `"${val}"`;
  if (typeof val === 'boolean') return val ? 'yes' : 'no';
  return String(val);
}

type ShallowDiff = {
  added: string[];
  removed: string[];
  changed: string[];
};

function shallowDiff(before: Record<string, unknown>, after: Record<string, unknown>): ShallowDiff {
  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const result: ShallowDiff = { added: [], removed: [], changed: [] };

  for (const key of allKeys) {
    const inBefore = key in before;
    const inAfter = key in after;

    if (!inBefore && inAfter) {
      result.added.push(key);
    } else if (inBefore && !inAfter) {
      result.removed.push(key);
    } else if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      result.changed.push(key);
    }
  }

  return result;
}

export type ChangePillVariant = 'added' | 'removed' | 'changed' | 'identity';

export type ChangePill = {
  variant: ChangePillVariant;
  label: string;
};

const MAX_PARTS = 3;

const FIELD_PRIORITY: Partial<Record<TargetType, string[]>> = {
  host: [
    'roleID',
    'tags',
    'isBlocked',
    'networkID',
    'ipAddresses',
    'ipAddress',
    'staticAddresses',
    'isLighthouse',
    'isRelay',
    'configOverrides',
    'name',
    'listenPort',
  ],
  role: ['firewallRules', 'name', 'description'],
  tag: ['firewallRules', 'routeSubscriptions', 'configOverrides', 'priority', 'name', 'description'],
  network: ['disableManagedLighthouses', 'lighthousesAsRelays', 'cidrV4', 'cidrV6', 'cidr', 'name'],
  endpointOIDC: ['roleID', 'domainID', 'networkID', 'enabled', 'authValidityHours', 'issuerURL', 'clientID', 'name'],
};

function getFieldPriority(field: string, targetType: TargetType): number {
  const priority = FIELD_PRIORITY[targetType] ?? [];
  const index = priority.indexOf(field);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

// Resource IDs are a lowercase type prefix plus 26 base32 characters (e.g. role-3XKJ2VHMQ7RB5NWC4TYD6PA8ZE).
// The value is opaque to readers, so only the fact that it changed is shown.
const RESOURCE_ID_PATTERN = /^[a-z]+-[A-Z0-9]{26}$/;

function isResourceID(val: unknown): val is string {
  return typeof val === 'string' && RESOURCE_ID_PATTERN.test(val);
}

function formatChangedSimpleValues(
  key: string,
  label: string,
  before: string | number | boolean,
  after: string | number | boolean
) {
  if (isResourceID(before) || isResourceID(after)) return `${label} changed`;
  if (key === 'disableManagedLighthouses' && typeof before === 'boolean' && typeof after === 'boolean') {
    return `${label}: ${formatSimpleValue(!before)} → ${formatSimpleValue(!after)}`;
  }
  return `${label}: ${formatSimpleValue(before)} → ${formatSimpleValue(after)}`;
}

function collectDiffPills(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  targetType: TargetType
): ChangePill[] {
  const diff = shallowDiff(before, after);
  const { added, removed, changed } = diff;
  const pills: Array<ChangePill & { field: string }> = [];

  for (const key of changed) {
    const bVal = before[key];
    const aVal = after[key];
    const label = getFieldLabel(targetType, key);

    if (Array.isArray(bVal) && Array.isArray(aVal)) {
      const delta = aVal.length - bVal.length;
      if (
        key === 'ipAddresses' &&
        bVal.every((item) => typeof item === 'string') &&
        aVal.every((item) => typeof item === 'string')
      ) {
        pills.push({ field: key, variant: 'changed', label: `${label}: ${bVal.join(', ')} → ${aVal.join(', ')}` });
      } else if (delta > 0) {
        pills.push({ field: key, variant: 'added', label: `+${delta} ${label}` });
      } else if (delta < 0) {
        pills.push({ field: key, variant: 'removed', label: `${delta} ${label}` });
      } else {
        pills.push({ field: key, variant: 'changed', label: `${label} changed` });
      }
    } else if (isSimpleValue(bVal) && isSimpleValue(aVal)) {
      pills.push({ field: key, variant: 'changed', label: formatChangedSimpleValues(key, label, bVal, aVal) });
    } else {
      pills.push({ field: key, variant: 'changed', label: `${label} changed` });
    }
  }

  for (const key of added) {
    pills.push({ field: key, variant: 'added', label: `${getFieldLabel(targetType, key)} added` });
  }

  for (const key of removed) {
    pills.push({ field: key, variant: 'removed', label: `${getFieldLabel(targetType, key)} removed` });
  }

  return pills
    .toSorted((a, b) => getFieldPriority(a.field, targetType) - getFieldPriority(b.field, targetType))
    .map((entry) => {
      return { variant: entry.variant, label: entry.label };
    });
}

function collectIdentityPills(snapshot: Record<string, unknown>, targetType: TargetType): ChangePill[] {
  const fields = IDENTITY_FIELDS[targetType] ?? ['name'];
  const pills: ChangePill[] = [];

  for (const field of fields) {
    const val = snapshot[field];
    const label = getFieldLabel(targetType, field);
    if (typeof val === 'string' && val.length > 0) {
      pills.push({ variant: 'identity', label: `${label}: ${val}` });
    } else if (field === 'ipAddresses' && Array.isArray(val) && val.every((item) => typeof item === 'string')) {
      pills.push(...val.map((address) => ({ variant: 'identity' as const, label: `${label}: ${address}` })));
    }
  }

  return pills;
}

function isObj(val: unknown): val is Record<string, unknown> {
  return val !== null && typeof val === 'object' && !Array.isArray(val);
}

// A cert v1 network's log entry has a single `cidr` and no IPv6 or managed-lighthouse fields; its v2 entry splits
// the CIDR into `cidrV4`/`cidrV6`. Updating logs a v1 `before` against a v2 `after`.
function isV1NetworkUpdate(before: Record<string, unknown>, after: Record<string, unknown>) {
  return 'cidr' in before && !('cidrV6' in before) && 'cidrV6' in after;
}

// v1 networks are always stored opted out of managed lighthouses, so the absent field means `true`.
function normalizeV1NetworkEntry({ cidr, ...rest }: Record<string, unknown>): Record<string, unknown> {
  return { ...rest, cidrV4: cidr, disableManagedLighthouses: true };
}

export function summarizeChangesStructured(log: AuditLog): ChangePill[] | null {
  const { event, target } = log;
  const behavior = EVENT_BEHAVIOR[event.type];

  if (behavior === 'skip') return null;

  if (behavior === 'host-state') {
    const snapshot = event.after ?? event.before;
    if (isObj(snapshot)) {
      const host = snapshot.host;
      if (isObj(host) && typeof host.name === 'string') {
        return [{ variant: 'identity', label: `"${host.name}"` }];
      }
    }
    return null;
  }

  if (behavior === 'create' && isObj(event.after)) {
    const pills = collectIdentityPills(event.after, target.type);
    return pills.length > 0 ? pills : null;
  }

  if (behavior === 'delete' && isObj(event.before)) {
    const pills = collectIdentityPills(event.before, target.type);
    return pills.length > 0 ? pills : null;
  }

  if (behavior === 'update' && isObj(event.before) && isObj(event.after)) {
    const before =
      target.type === 'network' && isV1NetworkUpdate(event.before, event.after) ?
        normalizeV1NetworkEntry(event.before)
      : event.before;
    const pills = collectDiffPills(before, event.after, target.type);
    if (pills.length === 0) return null;
    if (pills.length > MAX_PARTS) {
      const shown = pills.slice(0, MAX_PARTS);
      shown.push({ variant: 'changed', label: `+${pills.length - MAX_PARTS} more` });
      return shown;
    }
    return pills;
  }

  return null;
}
